import { parseISO } from "date-fns/parseISO";
import * as z from "zod";

import { notNullish } from "./filterGuards.js";

/** ISO date string */
export const DateString = z.string().brand("DateString");
export type DateString = z.infer<typeof DateString>;

export const getDateString = (date: Date): DateString =>
  date.toISOString().split("T")[0] as DateString;

// to improve this further, we could build a schema refinement
// that check that this is a string of an expected datetime format
/** ISO datetime string */
export const DateTimeString = z.string().brand("DateTimeString");
export type DateTimeString = z.infer<typeof DateTimeString>;

export const getDateTimeString = (date: Date): DateTimeString =>
  date.toISOString() as DateTimeString;

/** {@link getDateTimeString}, passing `null` through. */
export const getNullableDateTimeString = (
  date: Date | null,
): DateTimeString | null => (date == null ? null : getDateTimeString(date));

// https://github.com/typeorm/typeorm/issues/2176
// typeorm returns date columns as strings. in-memory db unit tests return them as Date objects though. Most entities
// are also typed as Dates today so this method is intended to unify all this under DateTimeString
export const uniformDateTimeString = (date: Date | string): DateTimeString => {
  let dateObj: Date;

  if (typeof date === "object" && date instanceof Date) {
    dateObj = date;
  } else {
    const dateMs = Date.parse(date);
    if (Number.isNaN(dateMs)) {
      throw new Error("Unable to parse date");
    }

    dateObj = new Date(dateMs);
  }

  return getDateTimeString(dateObj);
};

/** {@link uniformDateTimeString}, passing `null` through. */
export const uniformNullableDateTimeString = (
  date: Date | string | null,
): DateTimeString | null => (date == null ? null : uniformDateTimeString(date));

export const TimezoneName = z.string().brand("TimezoneName");
export type TimezoneName = z.infer<typeof TimezoneName>;

export const minDateTimeString = (
  datetimeStrings: (DateTimeString | null | undefined)[],
): Date | null => {
  const dates = datetimeStrings
    .filter(notNullish)
    .map((d) => parseISO(d).valueOf());

  if (dates.length === 0) return null;
  return new Date(Math.min(...dates));
};

export const zDateTimeString = z.string().transform((val, ctx) => {
  try {
    return uniformDateTimeString(val);
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid date-time string: ${val}`,
    });
    return z.NEVER;
  }
});

export const zTimezoneName = z.string().transform((val) => val as TimezoneName);
